// src/input/localPlayers.ts — Aynı ekranda 1–4 yerel oyuncu: oyuncu başına InputManager + GamepadSource + Rumble.
//
// Neden ayrı bir sınıf: bugüne kadar oyun TEK `InputManager` + `new GamepadSource(0)` + `new Rumble(0)` kullanıyordu
// (game.ts). Dört oyuncuya çıkarken bu üçlünün kopyalanması değil, SAHİPLİĞİN tek yerde toplanması gerekiyor —
// "hangi pad kimin", "pad koparsa ne olur", "klavye kimin" soruları tek dosyada yanıtlansın, menü ve maç aynı
// nesneyi paylaşsın (menüde seçilen oyuncular maça olduğu gibi girer).
//
// Sözleşme config/players.ts'ten okunur (MAX_LOCAL_PLAYERS, LocalPlayer, KEYBOARD_PLAYER); burada renk/sabit tablosu YOK.
//
// Üç kural bu dosyanın tasarımını belirledi:
//  1) **Pad kimliği HAM yuvadır** (`padIndex`), "kaçıncı bağlı pad" değil. Ordinal sıra oynaktır: ham yuvalar [0, 2] iken
//     0 koparsa "1. bağlı pad" bir anda ham 2 olur ve P1, P2'nin pad'ini çalar. Bu yüzden her oyuncunun GamepadSource'u
//     `'exact'` kipindedir (bkz. gamepad.ts PadPick) — pad koparsa oyuncu girdisi NÖTR olur, başka pad'e düşmez.
//     `padSlot` alanı yalnız GÖSTERİM içindir (menüde "2. pad"), her poll'da yeniden hesaplanır.
//  2) **Klavye yalnız P1'e bağlanır** (players.ts KEYBOARD_PLAYER). P1 = klavye + ilk pad BİRLİKTE: tek kişilik oyunda
//     klavyeyle gezip pad'e geçmek ayrı bir "katılma" istememeli. Bu yüzden P1'in pad'i yokken katılan İLK pad P1'e
//     bağlanır (`join` içindeki evlat edinme kuralı); ikinci pad P2 olur.
//  3) **Sıcak yolda tahsis yok**: dört yuva da kurulumda yaratılır (katılmamış olanlar da). Katılma/ayrılma yalnız
//     bayrak çevirir; `poll` sabit bir istek dizisini yeniden kullanır.
import { KEYBOARD_PLAYER, MAX_LOCAL_PLAYERS, type LocalPlayer } from '../config/players.ts';
import { inputFrame, type InputFrame } from '../core/types.ts';
import { GamepadSource, padButtonDown, padSlotOfIndex, pickPadSlot, type GamepadLike } from './gamepad.ts';
import { InputManager, type InputSource } from './inputManager.ts';
import { Rumble } from './rumble.ts';

/** Katılma düğmesi: A / Cross (standart eşleme 0; controls.ts PAD_BUTTONS[0] = Btn.Jump). Start maçı başlatır — o P1'in işi. */
export const PAD_JOIN_BUTTON = 0;

/**
 * Ayrıldıktan sonra aynı pad'in yeniden katılamayacağı süre (ms). B ile ayrılan oyuncunun parmağı A'ya değdiğinde
 * anında geri katılıp menüyü kilitlemesin; kısa ve fark edilmez.
 */
export const JOIN_COOLDOWN_MS = 300;

/**
 * Bir yerel oyuncunun çalışma zamanı yuvası: sözleşme alanları (LocalPlayer) + o oyuncuya ait girdi nesneleri.
 * Nesneler kurulumda yaratılır ve oyuncu ayrılsa bile YAŞAR (yeniden katılma tahsis yapmasın).
 */
export interface LocalPlayerSlot extends LocalPlayer {
  /** Bu oyuncunun girdi yöneticisi (maçta `consume` ile sim tick'ine, menüde `frame` ile arayüze gider). */
  readonly input: InputManager;
  /** Bu oyuncunun pad kaynağı ('exact' kipli; padIndex −1 iken sessiz). */
  readonly gamepad: GamepadSource;
  /** Bu oyuncunun titreşimi (yalnız kendi pad'ine gider). */
  readonly rumble: Rumble;
  /** Menü karesi: `pollFrames()` her rAF'ta buraya `consume` eder (maçta KULLANILMAZ — orada sim tick'i consume eder). */
  readonly frame: InputFrame;
  /** Oyuncu katıldı mı (menüde A'ya bastı / klavye oyuncusu). */
  joined: boolean;
  /** Girdi kaynağı şu an çalışıyor mu: pad bağlı ya da (P1 için) klavye takılı. Pad koparsa false → girdi nötr. */
  connected: boolean;
  /** HAM navigator.getGamepads() yuvası; −1 = pad yok (klavye oyuncusu ya da pad koptu). Kimlik BU alandır. */
  padIndex: number;
}

/** Boş pad dizisi (DOM yokken tahsis etmemek için tek nesne). */
const NO_PADS: ReadonlyArray<GamepadLike | null> = [];

/** navigator.getGamepads() ya da boş dizi (Node / eski tarayıcı). */
function readPads(): ArrayLike<GamepadLike | null> {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return NO_PADS;
  return navigator.getGamepads() as unknown as ArrayLike<GamepadLike | null>;
}

/**
 * Yerel oyuncu kadrosu. Kullanım (menü): her rAF `const joins = lp.poll(now)` → istekleri `lp.join(padIndex)` ile karşıla,
 * `lp.pollFrames()` ile her oyuncunun imlecini sür. Kullanım (maç): her rAF `lp.poll(now)` + `lp.pollInputs()`,
 * her sim tick'inde `p.input.consume(inputOf(world, p.fighterId))`.
 */
export class LocalPlayers {
  /** Dört yuvanın tamamı (katılmamışlar dahil; index = renk sırası). Menü "boş yuva" göstermek için okuyabilir. */
  readonly slots: readonly LocalPlayerSlot[];
  /** Titreşim genel anahtarı (ayar menüsü). Kapalıyken hiçbir oyuncunun pad'i titremez. */
  private rumbleOn = true;

  /** Katılmış oyuncular, index sırasında (getter bunu döndürür — kare başı tahsis yok). */
  private readonly joined: LocalPlayerSlot[] = [];
  /** Klavye+mouse kaynağı (yalnız P1'in InputManager'ında). */
  private keyboardSource: InputSource | null = null;
  /** Ham yuva başına önceki A durumu (katılma kenarı). Dizi pad sayısına göre büyür; sıcak yolda tahsis yok. */
  private readonly prevJoinBtn: boolean[] = [];
  /** Ham yuva başına "bu zamandan önce katılamaz" (ms) — ayrılma sonrası kısa bekleme. */
  private readonly joinCooldown: number[] = [];
  /** `poll`un döndürdüğü katılma istekleri (aynı dizi yeniden kullanılır — çağıran saklamamalı). */
  private readonly requests: number[] = [];
  /** Son `poll(now)` zamanı (ayrılma bekleme süresini kurmak için). */
  private lastNow = 0;

  constructor() {
    const slots: LocalPlayerSlot[] = [];
    for (let i = 0; i < MAX_LOCAL_PLAYERS; i++) {
      const input = new InputManager();
      // 'exact': pad koparsa BAŞKA pad'e düşme (bkz. dosya başı kural 1). padIndex −1 iken kaynak sessizdir.
      const gamepad = new GamepadSource(-1, 'exact');
      input.addSource(gamepad);
      const rumble = new Rumble(-1);
      rumble.enabled = false;   // pad bağlanınca açılır (yanlış yuvaya titreşim gitmesin)
      slots.push({
        index: i, fighterId: -1, padSlot: -1, charIdx: 0, ready: false,
        input, gamepad, rumble, frame: inputFrame(),
        joined: false, connected: false, padIndex: -1,
      });
    }
    this.slots = slots;
  }

  /** Katılmış oyuncular (index sırasında). Menü ve maç bunu gezer. */
  get players(): readonly LocalPlayerSlot[] { return this.joined; }
  /** Katılmış oyuncu sayısı (kadro hesabı: insan + bot ≤ 8). */
  get count(): number { return this.joined.length; }
  /** Kadro dolu mu (4 oyuncu). */
  get full(): boolean { return this.joined.length >= MAX_LOCAL_PLAYERS; }
  /** Menüde DÖVÜŞ koşulu: en az bir oyuncu var ve KATILAN HERKES hazır (DESIGN kararı 4). */
  get allReady(): boolean { return this.joined.length > 0 && this.joined.every((p) => p.ready); }

  /** index'e göre yuva (katılmamış olabilir). */
  playerAt(index: number): LocalPlayerSlot | undefined { return this.slots[index]; }

  /** Bu ham pad yuvası kimin? (katılmamışsa null) */
  padOwner(padIndex: number): LocalPlayerSlot | null {
    if (padIndex < 0) return null;
    for (const p of this.joined) if (p.padIndex === padIndex) return p;
    return null;
  }

  /** Bu dövüşçü yerel bir oyuncu mu (kamera sarsıntısı / HUD kararları: "yerel oyunculardan birini ilgilendiriyor mu"). */
  playerOfFighter(fighterId: number): LocalPlayerSlot | null {
    if (fighterId < 0) return null;
    for (const p of this.joined) if (p.fighterId === fighterId) return p;
    return null;
  }
  isLocalFighter(fighterId: number): boolean { return this.playerOfFighter(fighterId) !== null; }

  /** Maç kurulurken dövüşçü kimliğini bağla (menü sırası → world.fighters sırası). */
  setFighterId(index: number, fighterId: number): void {
    const p = this.slots[index];
    if (p) p.fighterId = fighterId;
  }

  /**
   * Klavye+mouse kaynağını P1'e bağlar (P2–P4'e klavye BAĞLANMAZ — aynı klavyeyi paylaşan iki kişi birbirinin
   * tuşlarını basar). `join` true ise P1 aynı anda katılmış sayılır (klavyeyle oynayan tek kişi menüde A beklemesin).
   */
  attachKeyboard(source: InputSource, join = true): LocalPlayerSlot {
    const p = this.slots[KEYBOARD_PLAYER]!;
    if (this.keyboardSource === source) { if (join && !p.joined) this.joinSlot(p); return p; }
    if (this.keyboardSource) p.input.removeSource(this.keyboardSource);
    this.keyboardSource = source;
    p.input.addSource(source);
    if (join && !p.joined) this.joinSlot(p);
    else if (p.joined) p.connected = true;
    return p;
  }

  /** Klavyeyi bırak (menüden çıkış / maç sonu). Oyuncu katılmış kalır — pad'i varsa oynamayı sürdürür. */
  detachKeyboard(): void {
    const p = this.slots[KEYBOARD_PLAYER]!;
    if (this.keyboardSource) p.input.removeSource(this.keyboardSource);
    this.keyboardSource = null;
    if (p.joined && p.padIndex < 0) p.connected = false;
  }

  /**
   * Her kare bir kez: pad bağlantılarını tazeler ve KATILMA isteklerini bildirir.
   * Dönüş: bu karede A'ya basan, HENÜZ BİR OYUNCUYA BAĞLI OLMAYAN pad'lerin HAM yuvaları — `join(padIndex)` ile karşıla.
   * Dizi yeniden kullanılır: çağıran hemen tüketmeli (saklarsa bir sonraki poll'da değişir).
   */
  poll(now = 0): readonly number[] {
    this.lastNow = now;
    this.requests.length = 0;
    const pads = readPads();

    // 1) Bağlantı durumu: pad koptuysa oyuncu "bağlantı yok" olur (maç sürer, girdisi nötrdür), yeniden takılırsa döner.
    for (const p of this.joined) {
      if (p.padIndex < 0) {
        // Pad'siz oyuncu ancak P1 olabilir (klavye). Klavye de yoksa girdi kaynağı kalmadı.
        p.connected = p.index === KEYBOARD_PLAYER && this.keyboardSource !== null;
        p.padSlot = -1;
        continue;
      }
      const on = !!(p.padIndex < pads.length ? pads[p.padIndex] : null);
      if (on !== p.connected) {
        p.connected = on;
        // Kopma anında bekleyen kenarları düşür: pad çıkarıldığında basılı kalan tuş sonsuza dek basılı görünmesin.
        if (!on) p.input.reset();
      }
      p.padSlot = on ? padSlotOfIndex(pads, p.padIndex) : -1;
      // Titreşim oyuncunun KENDİ pad'ine gider; pad yokken kapalı (Rumble'ın "ilk bağlı pad"e düşmesi başkasını titretirdi).
      p.rumble.padIndex = p.padIndex;
      p.rumble.enabled = this.rumbleOn && on;
    }

    // 2) Katılma kenarı: sahipsiz her pad'in A basışı bir istek üretir (kenar — basılı tutmak tekrar etmez).
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i] ?? null;
      const down = padButtonDown(pad, PAD_JOIN_BUTTON);
      const prev = this.prevJoinBtn[i] === true;
      this.prevJoinBtn[i] = down;
      if (!down || prev || !pad) continue;
      if (this.padOwner(i)) continue;                       // zaten bir oyuncunun pad'i (A onun "hazır"ı)
      if (now < (this.joinCooldown[i] ?? 0)) continue;      // yeni ayrıldı — kısa bekleme
      this.requests.push(i);
    }
    return this.requests;
  }

  /**
   * Bir pad'i oyuncuya bağlar. `padIndex` HAM yuvadır (poll'un döndürdüğü değer); −1 verilirse pad'siz (klavye) oyuncu.
   * Evlat edinme kuralı: P1 klavyeyle katılmış ve pad'i yoksa, İLK pad P1'e bağlanır (tek kişilik oyun bugünkü gibi).
   * Dönüş: oyuncu yuvası; kadro doluysa null. Pad zaten birindeyse o oyuncu döner (çift katılma yok).
   */
  join(padIndex = -1, adoptIntoKeyboardPlayer = true): LocalPlayerSlot | null {
    const owner = this.padOwner(padIndex);
    if (owner) return owner;

    const kb = this.slots[KEYBOARD_PLAYER]!;
    if (padIndex >= 0 && adoptIntoKeyboardPlayer && kb.joined && kb.padIndex < 0 && this.keyboardSource !== null) {
      this.bindPad(kb, padIndex);
      return kb;
    }
    for (const p of this.slots) {
      if (p.joined) continue;
      // Klavye P1'e ait: pad'li bir oyuncu P1 yuvasına düşerse klavyeyi de paylaşır — bu istenen davranış (aynı kişi).
      this.joinSlot(p);
      if (padIndex >= 0) this.bindPad(p, padIndex);
      return p;
    }
    return null;   // dolu (MAX_LOCAL_PLAYERS)
  }

  /**
   * BELİRLİ bir oyuncu numarasına katılma (menüden gelen kadro: numara = renk ve HUD sırası, korunmalı).
   * `join`den farkı: boş yuvaya değil, İSTENEN yuvaya bağlar ve P1 evlat edinme kuralını uygulamaz — menüde P3 olan
   * kişi maçta da P3 (yeşil) kalsın, arada biri ayrıldı diye numarası kaymasın (numaralar boşluklu olabilir).
   * Dönüş: yuva; numara geçersiz / yuva başkasında / pad başkasınınsa null.
   */
  joinAt(index: number, padIndex = -1): LocalPlayerSlot | null {
    const p = this.slots[index];
    if (!p) return null;
    if (p.joined) return p.padIndex === padIndex ? p : null;   // zaten kadroda: aynı pad'se sorun yok, değilse çakışma
    if (padIndex >= 0 && this.padOwner(padIndex)) return null;  // pad başka oyuncuda
    this.joinSlot(p);
    if (padIndex >= 0) this.bindPad(p, padIndex);
    return p;
  }

  /**
   * Oyuncunun pad'ini bırakır; oyuncu KADRODA KALIR (girdisi nötrleşir, klavyesi varsa sürer).
   * Kullanımı tek: P1'in pad'i kopunca (maç içinde çıkarılıp başka yuvaya takılabilir) kimliği serbest bırakılır ki
   * yeniden takılan pad evlat edinilebilsin — tek kişilik oyunun "pad'i tak, oynamaya devam et" davranışı korunur.
   * P2–P4 için ÇAĞRILMAZ: onların pad kimliği sabit kalmalı, aksi hâlde biri diğerinin pad'ini kapabilir.
   */
  unbindPad(index: number): boolean {
    const p = this.slots[index];
    if (!p || p.padIndex < 0) return false;
    p.rumble.enabled = false;
    p.rumble.stop();
    p.rumble.padIndex = -1;
    p.gamepad.padIndex = -1;
    p.padIndex = -1;
    p.padSlot = -1;
    p.connected = p.index === KEYBOARD_PLAYER && this.keyboardSource !== null;
    p.input.reset();
    return true;
  }

  /** "N. bağlı pad" ile katılma (menü listesi ordinal düşünüyorsa). Yuva boşsa null. */
  joinByPadSlot(slot: number): LocalPlayerSlot | null {
    const raw = pickPadSlot(readPads(), slot);
    return raw >= 0 ? this.join(raw) : null;
  }

  /**
   * Oyuncuyu kadrodan çıkarır (menüde kilitsizken B). Girdi nesneleri kalır, durumu sıfırlanır; pad'i serbest kalır
   * (başkası ya da aynı kişi yeniden katılabilir — kısa bekleme sonrası).
   */
  leave(index: number): boolean {
    const p = this.slots[index];
    if (!p || !p.joined) return false;
    if (p.padIndex >= 0) this.joinCooldown[p.padIndex] = this.lastNow + JOIN_COOLDOWN_MS;
    p.rumble.enabled = false;
    p.rumble.stop();
    p.rumble.padIndex = -1;
    p.gamepad.padIndex = -1;
    p.padIndex = -1;
    p.padSlot = -1;
    p.joined = false;
    p.connected = false;
    p.ready = false;
    p.fighterId = -1;
    p.input.reset();
    this.rebuild();
    return true;
  }

  /** Herkesi çıkar (menüye dönüş / yeni oturum). Klavye bağı korunur. */
  leaveAll(): void {
    for (const p of this.slots) if (p.joined) this.leave(p.index);
  }

  /** Her kare bir kez (MAÇ): her katılan oyuncunun kaynaklarını oku. Kenarlar `consume`a kadar birikir. */
  pollInputs(): void {
    for (const p of this.joined) p.input.poll();
  }

  /**
   * Her kare bir kez (MENÜ): kaynakları oku ve her oyuncunun `frame`ine tüket — imleç hareketi ve A/B kenarları
   * doğrudan `p.frame.pressed` üzerinden okunur. MAÇTA KULLANMA: orada `consume`u sim tick'i yapar (kenarlar çalınır).
   */
  pollFrames(): void {
    for (const p of this.joined) { p.input.poll(); p.input.consume(p.frame); }
  }

  /** Titreşim genel anahtarı (ayar). Kapalıyken pad'i olan oyuncular da titremez. */
  setRumbleEnabled(on: boolean): void {
    this.rumbleOn = on;
    for (const p of this.joined) p.rumble.enabled = on && p.connected && p.padIndex >= 0;
  }

  /** Duraklatma / maç sonu: bekleyen kenarları düşür, titreşimi kes (WP6 kuralı, tek oyuncudaki `input.reset()` eşleniği). */
  reset(): void {
    for (const p of this.joined) { p.input.reset(); p.rumble.stop(); }
  }

  /* ------------------------------------------------------------------ iç */

  private joinSlot(p: LocalPlayerSlot): void {
    p.joined = true;
    p.ready = false;
    p.fighterId = -1;
    p.connected = p.padIndex >= 0 || (p.index === KEYBOARD_PLAYER && this.keyboardSource !== null);
    p.input.reset();
    this.rebuild();
  }

  private bindPad(p: LocalPlayerSlot, padIndex: number): void {
    p.padIndex = padIndex;
    p.gamepad.padIndex = padIndex;     // kip 'exact' — yalnız bu yuva
    p.padSlot = padSlotOfIndex(readPads(), padIndex);
    p.rumble.padIndex = padIndex;
    p.rumble.enabled = this.rumbleOn;
    p.connected = true;
    // Katılma basışı (A) menüye/maça "yeni basış" olarak SIZMASIN. reset() tek başına yetmez: prevButtons 0 kalır ve
    // bir sonraki poll'da hâlâ basılı olan A yeniden KENAR üretirdi (katılan oyuncu istemeden "hazır" olurdu).
    // Bu yüzden bir kez poll (prevButtons = o anki basılılar) + consume (biriken kenarı çöpe at) yapılır.
    p.input.reset();
    p.input.poll();
    p.input.consume(p.frame);
  }

  /** `joined` listesini index sırasında yeniden kurar (soğuk yol: yalnız katılma/ayrılmada). */
  private rebuild(): void {
    this.joined.length = 0;
    for (const p of this.slots) if (p.joined) this.joined.push(p);
  }
}
